/**
 * InsightPro™ — Admin: Client Management  v1.0
 * Route: /admin/clients  (SuperAdmin only)
 *
 * Lists all provisioned companies from Firestore.
 * Click a client → /admin/clients/:companyId
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getFirestore, collection, getDocs, query, orderBy } from 'firebase/firestore';

const db = getFirestore();

const STATUS_COLOR = {
  active:     { bg: 'rgba(26,173,99,0.1)',  text: 'var(--green-l)',  label: 'Active'     },
  onboarding: { bg: 'rgba(196,122,26,0.1)', text: 'var(--amber)',    label: 'Onboarding' },
  suspended:  { bg: 'rgba(176,40,40,0.1)',  text: 'var(--red)',      label: 'Suspended'  },
  paused:     { bg: 'rgba(136,136,150,0.1)',text: 'var(--page-muted)',label: 'Paused'     },
};

const PLAN_COLOR = {
  essential:     { text: 'var(--page-muted)',  label: 'Essential'     },
  professional:  { text: 'var(--gold)',        label: 'Professional'  },
  enterprise:    { text: 'var(--gold-light)',  label: 'Enterprise'    },
};

function Badge({ status }) {
  const s = STATUS_COLOR[status] || STATUS_COLOR.onboarding;
  return (
    <span style={{
      fontFamily: 'var(--font-mono)', fontSize: 9, letterSpacing: '0.12em',
      textTransform: 'uppercase', color: s.text,
      background: s.bg, padding: '3px 8px', borderRadius: 2,
    }}>{s.label}</span>
  );
}

function PlanBadge({ plan }) {
  const p = PLAN_COLOR[plan] || PLAN_COLOR.essential;
  return (
    <span style={{ fontFamily: 'var(--font-mono)', fontSize: 9, color: p.text }}>
      {p.label}
    </span>
  );
}

export default function AdminClients() {
  const navigate = useNavigate();
  const [clients,  setClients]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [search,   setSearch]   = useState('');
  const [filter,   setFilter]   = useState('all');
  const [error,    setError]    = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const snap = await getDocs(query(collection(db, 'companies'), orderBy('createdAt', 'desc')));
        setClients(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const filtered = clients.filter(c => {
    const matchSearch = !search ||
      c.name?.toLowerCase().includes(search.toLowerCase()) ||
      c.slug?.toLowerCase().includes(search.toLowerCase());
    const matchFilter = filter === 'all' || c.status === filter;
    return matchSearch && matchFilter;
  });

  const counts = {
    all:        clients.length,
    active:     clients.filter(c => c.status === 'active').length,
    onboarding: clients.filter(c => c.status === 'onboarding').length,
    suspended:  clients.filter(c => c.status === 'suspended').length,
  };

  const S = {
    page:   { padding: '28px 32px', animation: 'fadeUp 0.4s ease both' },
    header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 28 },
    title:  { fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 700, color: 'var(--page-text)', letterSpacing: '-0.02em' },
    sub:    { fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--page-muted)', marginTop: 3, letterSpacing: '0.08em' },
    newBtn: {
      fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase',
      background: 'var(--gold)', color: 'var(--void)', border: 'none',
      padding: '9px 18px', cursor: 'pointer', borderRadius: 2,
    },
    statsRow: { display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap' },
    statCard: (active) => ({
      background: active ? 'var(--card)' : 'var(--card)',
      border: active ? '1px solid var(--gold)' : '1px solid var(--card-border)',
      padding: '12px 18px', cursor: 'pointer', borderRadius: 2, minWidth: 100,
    }),
    statN:  { fontFamily: 'var(--font-display)', fontSize: 24, fontWeight: 700, color: 'var(--page-text)', lineHeight: 1 },
    statL:  { fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--page-muted)', marginTop: 4, letterSpacing: '0.1em', textTransform: 'uppercase' },
    toolbar: { display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' },
    search: {
      flex: 1, minWidth: 200,
      background: 'var(--card)', border: '1px solid var(--card-border)',
      padding: '9px 14px', fontFamily: 'var(--font-body)', fontSize: 13,
      color: 'var(--page-text)', outline: 'none', borderRadius: 2,
    },
    table:  { background: 'var(--card)', border: '1px solid var(--card-border)', borderRadius: 4, overflow: 'hidden' },
    thead:  { background: 'var(--page)', borderBottom: '1px solid var(--card-border)' },
    th:     { fontFamily: 'var(--font-mono)', fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--page-muted)', padding: '10px 16px', textAlign: 'left' },
    tr:     { borderBottom: '1px solid var(--card-border)', cursor: 'pointer', transition: 'background 0.15s' },
    td:     { padding: '13px 16px', fontSize: 13, color: 'var(--page-text)', verticalAlign: 'middle' },
    tdMuted:{ padding: '13px 16px', fontSize: 12, color: 'var(--page-muted)', verticalAlign: 'middle', fontFamily: 'var(--font-mono)' },
    empty:  { padding: '48px', textAlign: 'center', color: 'var(--page-muted)', fontFamily: 'var(--font-mono)', fontSize: 11 },
  };

  return (
    <div style={S.page}>
      <div style={S.header}>
        <div>
          <div style={S.title}>Client Management</div>
          <div style={S.sub}>{clients.length} companies · SuperAdmin</div>
        </div>
        <button style={S.newBtn} onClick={() => navigate('/admin/onboard')}>
          + Onboard New Client
        </button>
      </div>

      {/* Stats strip */}
      <div style={S.statsRow}>
        {[['all','All'], ['active','Active'], ['onboarding','Onboarding'], ['suspended','Suspended']].map(([k, l]) => (
          <div key={k} style={S.statCard(filter === k)} onClick={() => setFilter(k)}>
            <div style={S.statN}>{counts[k]}</div>
            <div style={S.statL}>{l}</div>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div style={S.toolbar}>
        <input
          style={S.search}
          placeholder="Search by company name or slug..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      {/* Table */}
      {loading ? (
        <div style={S.empty}>Loading clients...</div>
      ) : error ? (
        <div style={{ ...S.empty, color: 'var(--red)' }}>{error}</div>
      ) : (
        <div style={S.table}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={S.thead}>
              <tr>
                {['Company', 'Slug', 'Industry', 'Plan', 'PBI Config', 'Status', 'Created'].map(h => (
                  <th key={h} style={S.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={7} style={S.empty}>No clients found</td></tr>
              ) : filtered.map(c => (
                <tr
                  key={c.id}
                  style={S.tr}
                  onClick={() => navigate(`/admin/clients/${c.id}`)}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--page)'}
                  onMouseLeave={e => e.currentTarget.style.background = ''}
                >
                  <td style={S.td}>
                    <div style={{ fontWeight: 500 }}>{c.name}</div>
                    {c.tradingName && c.tradingName !== c.name && (
                      <div style={{ fontSize: 11, color: 'var(--page-muted)', marginTop: 2 }}>{c.tradingName}</div>
                    )}
                  </td>
                  <td style={S.tdMuted}>{c.slug}</td>
                  <td style={S.tdMuted}>{c.industry || '—'}</td>
                  <td style={S.td}><PlanBadge plan={c.plan} /></td>
                  <td style={S.td}>
                    {c.pbi?.workspaceId ? (
                      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--green-l)' }}>✓ Configured</span>
                    ) : (
                      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--amber)' }}>⚠ Not set</span>
                    )}
                  </td>
                  <td style={S.td}><Badge status={c.status} /></td>
                  <td style={S.tdMuted}>
                    {c.createdAt?.toDate?.()?.toLocaleDateString('en-ZA') || '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
